import { useState } from "react";
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useSession } from "@/lib/session";
import { Button } from "@/ui/Button";
import { Checkbox, Field, Input } from "@/ui/Field";
import { AuthCard, FormError } from "@/features/auth/AuthCard";
import { isSafeRedirect } from "@/lib/redirect";

export const Route = createFileRoute("/_public/sign-in")({
  component: SignInScreen,
  // Search params are declared optional so that a plain <Link to="/sign-in">
  // elsewhere in the app does not have to spell out an empty redirect.
  // An authentication screen is never a place to come back to, whoever put it
  // in the address — this guard, a stale bookmark, or a link someone was sent.
  // Refused at parse time, so no screen can act on one. See lib/redirect.ts.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === "string" && isSafeRedirect(search.redirect)
      ? { redirect: search.redirect }
      : {},
});

interface SignInResponse {
  mfa_required: boolean;
  expires_at: string;
}

function SignInScreen() {
  const navigate = useNavigate();
  const router = useRouter();
  const { refresh } = useSession();
  const { redirect } = Route.useSearch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Unticked by default and deliberately so: the cost of the default being
  // wrong is not symmetric. A session that ends too early is an inconvenience;
  // one that outlives somebody at a borrowed machine is an incident.
  const [remember, setRemember] = useState(false);

  const signIn = useMutation({
    mutationFn: () => api.post<SignInResponse>("/auth/sign-in", { email, password, remember }),
    // The session is reloaded before navigating, in both branches.
    //
    // # WHY THE SECOND-FACTOR BRANCH NEEDED IT TOO
    //
    // It used to navigate straight to /two-factor without reloading, and that
    // put people back on this form. The session query still held the 401 from
    // before they signed in, so the authenticated layout — still mounted while
    // the router moves, and re-running its effect on every pathname change —
    // saw no user and no pending factor, and navigated to /sign-in with
    // `replace: true`, overwriting the jump to the code screen.
    //
    // It was reported as "sometimes I enter the right code and end up back at
    // the login window", and the audit log agreed: pairs of successful sign-ins
    // three to twenty-seven seconds apart, on every session, for weeks. The
    // password was never the problem and no second factor was ever rejected.
    //
    // After the reload the session says the factor is outstanding, so the
    // layout sends people to /two-factor itself. The navigation below and the
    // guard now want the same thing, which is why they stop fighting.
    onSuccess: async (result) => {
      await refresh();
      await router.invalidate();
      if (result.mfa_required) {
        await navigate({ to: "/two-factor", search: { redirect } });
        return;
      }
      await navigate({ to: redirect ?? "/" });
    },
  });

  const fieldErrors = signIn.error instanceof ApiError ? signIn.error.fieldErrors() : {};
  const unverified =
    signIn.error instanceof ApiError && signIn.error.code === "precondition_failed";

  return (
    <AuthCard
      title="Sign in"
      subtitle="Continue to your Drumlin projects."
      footer={
        <>
          New here?{" "}
          <Link to="/register" className="font-medium text-[var(--primary)] hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          signIn.mutate();
        }}
      >
        <Field label="Email address" error={fieldErrors.email}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={describedBy}
              invalid={invalid}
            />
          )}
        </Field>

        <Field
          label="Password"
          error={fieldErrors.password}
          hint={
            <Link to="/forgot-password" className="text-[var(--primary)] hover:underline">
              Forgotten your password?
            </Link>
          }
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby={describedBy}
              invalid={invalid}
            />
          )}
        </Field>

        <Checkbox
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          label="Stay signed in on this device"
        />

        {signIn.error && !Object.keys(fieldErrors).length && (
          <FormError error={signIn.error}>
            {unverified && (
              <Link
                to="/verify-email"
                search={{ email }}
                className="mt-1 inline-block font-medium underline"
              >
                Send another confirmation email
              </Link>
            )}
          </FormError>
        )}

        <Button type="submit" tone="primary" wide busy={signIn.isPending}>
          Sign in
        </Button>
      </form>
    </AuthCard>
  );
}
